import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Grid,
  Card, CardContent, Switch, FormControlLabel, Tooltip, Tab, Tabs, LinearProgress,
  Divider, InputAdornment
} from '@mui/material';
import {
  Add, Edit, Delete, LocalOffer, Percent, AttachMoney, LocalShipping,
  ContentCopy, ToggleOn, ToggleOff, TrendingUp, ShoppingCart, CalendarToday,
  Refresh, Close, CheckCircle, CardGiftcard, People, AutoAwesome, Send
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { getOffersAPI, createOfferAPI, updateOfferAPI, deleteOfferAPI, toggleOfferAPI, getOfferAnalyticsAPI, generateAIOfferSuggestionsAPI, analyzeOfferPerformanceAIAPI } from './crmAPI';
import SendOfferDialog from './SendOfferDialog';

const OffersManagement = () => {
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestionsOpen, setAiSuggestionsOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', code: '', type: 'PERCENTAGE', value: '',
    minOrderValue: '', maxDiscount: '', startDate: '', endDate: '',
    maxUsageTotal: '', maxUsagePerCustomer: 1, isActive: true
  });

  const offerTypes = [
    { value: 'PERCENTAGE', label: 'Percentage Off', icon: <Percent /> },
    { value: 'FIXED_AMOUNT', label: 'Fixed Amount Off', icon: <AttachMoney /> },
    { value: 'BUY_X_GET_Y', label: 'Buy X Get Y', icon: <CardGiftcard /> },
    { value: 'FREE_DELIVERY', label: 'Free Delivery', icon: <LocalShipping /> }
  ];

  useEffect(() => { fetchOffers(); }, []);
  useEffect(() => { filterOffers(); }, [offers, tabValue, searchTerm]);

  const fetchOffers = async () => { setOffers(await getOffersAPI(setLoading)); };

  const filterOffers = () => {
    let filtered = [...offers];
    const now = new Date();
    if (tabValue === 1) filtered = filtered.filter(o => o.isActive && new Date(o.endDate) >= now && new Date(o.startDate) <= now);
    else if (tabValue === 2) filtered = filtered.filter(o => new Date(o.startDate) > now);
    else if (tabValue === 3) filtered = filtered.filter(o => new Date(o.endDate) < now);
    else if (tabValue === 4) filtered = filtered.filter(o => !o.isActive);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o => o.title?.toLowerCase().includes(term) || o.code?.toLowerCase().includes(term));
    }
    setFilteredOffers(filtered);
  };

  const handleOpenDialog = (offer = null) => {
    if (offer) {
      setSelectedOffer(offer);
      setFormData({
        title: offer.title || '', description: offer.description || '', code: offer.code || '',
        type: offer.type || 'PERCENTAGE', value: offer.value || '', minOrderValue: offer.minOrderValue || '',
        maxDiscount: offer.maxDiscount || '', startDate: offer.startDate ? new Date(offer.startDate).toISOString().split('T')[0] : '',
        endDate: offer.endDate ? new Date(offer.endDate).toISOString().split('T')[0] : '',
        maxUsageTotal: offer.maxUsageTotal || '', maxUsagePerCustomer: offer.maxUsagePerCustomer || 1, isActive: offer.isActive !== false
      });
    } else {
      setSelectedOffer(null);
      setFormData({ title: '', description: '', code: '', type: 'PERCENTAGE', value: '', minOrderValue: '', maxDiscount: '',
        startDate: new Date().toISOString().split('T')[0], endDate: '', maxUsageTotal: '', maxUsagePerCustomer: 1, isActive: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setSelectedOffer(null); };
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const generatePromoCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    setFormData(prev => ({ ...prev, code }));
  };

  const handleSubmit = async () => {
    if (!formData.title) { Swal.fire('Error', 'Please enter offer title', 'error'); return; }
    if (!formData.value || formData.value <= 0) { Swal.fire('Error', 'Please enter a valid discount value', 'error'); return; }
    if (!formData.startDate || !formData.endDate) { Swal.fire('Error', 'Please select start and end dates', 'error'); return; }
    if (new Date(formData.endDate) < new Date(formData.startDate)) { Swal.fire('Error', 'End date must be after start date', 'error'); return; }

    const payload = { ...formData, value: Number(formData.value), minOrderValue: formData.minOrderValue ? Number(formData.minOrderValue) : 0,
      maxDiscount: formData.maxDiscount ? Number(formData.maxDiscount) : null, maxUsageTotal: formData.maxUsageTotal ? Number(formData.maxUsageTotal) : null,
      maxUsagePerCustomer: Number(formData.maxUsagePerCustomer) || 1 };

    const result = selectedOffer ? await updateOfferAPI(selectedOffer._id, payload, setLoading) : await createOfferAPI(payload, setLoading);
    if (result.success) { Swal.fire('Success', selectedOffer ? 'Offer updated!' : 'Offer created!', 'success'); handleCloseDialog(); fetchOffers(); }
    else { Swal.fire('Error', result.message, 'error'); }
  };

  const handleDelete = async (offer) => {
    const result = await Swal.fire({ title: 'Delete Offer?', text: `Are you sure you want to delete "${offer.title}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete' });
    if (result.isConfirmed) {
      if (await deleteOfferAPI(offer._id, setLoading)) { Swal.fire('Deleted!', 'Offer has been deleted.', 'success'); fetchOffers(); }
      else { Swal.fire('Error', 'Failed to delete offer', 'error'); }
    }
  };

  const handleToggle = async (offer) => { if (await toggleOfferAPI(offer._id, setLoading)) fetchOffers(); };
  const handleViewAnalytics = async (offer) => { setSelectedOffer(offer); setAnalytics(await getOfferAnalyticsAPI(offer._id, setLoading)); setAnalyticsDialogOpen(true); };
  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Code copied!', showConfirmButton: false, timer: 1500 }); };

  // Open Send Offer Dialog
  const handleSendOffer = (offer) => {
    setSelectedOffer(offer);
    setSendDialogOpen(true);
  };

  // AI Generate Suggestions
  const handleGenerateAISuggestions = async () => {
    setAiLoading(true);
    try {
      const suggestions = await generateAIOfferSuggestionsAPI({
        businessName: 'Restaurant',
        businessType: 'restaurant',
        currentOffers: offers.map(o => ({ title: o.title })),
        seasonalContext: new Date().toLocaleString('default', { month: 'long' })
      }, setAiLoading);
      
      if (suggestions && !suggestions.raw) {
        setAiSuggestions(suggestions);
        setAiSuggestionsOpen(true);
      } else {
        Swal.fire('AI Error', 'Could not generate suggestions. Please check your Gemini API key in settings.', 'warning');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to generate AI suggestions', 'error');
    }
    setAiLoading(false);
  };

  const applyAISuggestion = (suggestion) => {
    const startDate = new Date().toISOString().split('T')[0];
    const endDate = new Date(Date.now() + (suggestion.validityDays || 7) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setFormData({
      title: suggestion.title || '',
      description: suggestion.description || '',
      code: suggestion.suggestedCode || '',
      type: suggestion.type || 'PERCENTAGE',
      value: suggestion.value || '',
      minOrderValue: suggestion.minOrderValue || '',
      maxDiscount: suggestion.maxDiscount || '',
      startDate,
      endDate,
      maxUsageTotal: '',
      maxUsagePerCustomer: 1,
      isActive: true
    });
    setAiSuggestionsOpen(false);
    setDialogOpen(true);
  };

  const getOfferStatus = (offer) => {
    const now = new Date(), start = new Date(offer.startDate), end = new Date(offer.endDate);
    if (!offer.isActive) return { label: 'Inactive', color: 'default' };
    if (now < start) return { label: 'Scheduled', color: 'info' };
    if (now > end) return { label: 'Expired', color: 'error' };
    return { label: 'Active', color: 'success' };
  };

  const getOfferTypeIcon = (type) => {
    if (type === 'PERCENTAGE') return <Percent fontSize="small" />;
    if (type === 'FIXED_AMOUNT') return <AttachMoney fontSize="small" />;
    if (type === 'BUY_X_GET_Y') return <CardGiftcard fontSize="small" />;
    if (type === 'FREE_DELIVERY') return <LocalShipping fontSize="small" />;
    return <LocalOffer fontSize="small" />;
  };

  const formatOfferValue = (offer) => {
    if (offer.type === 'PERCENTAGE') return `${offer.value}% OFF`;
    if (offer.type === 'FIXED_AMOUNT') return `${offer.value} OFF`;
    if (offer.type === 'BUY_X_GET_Y') return `Buy ${offer.value} Get 1`;
    if (offer.type === 'FREE_DELIVERY') return 'Free Delivery';
    return offer.value;
  };

  const stats = {
    total: offers.length,
    active: offers.filter(o => o.isActive && new Date(o.endDate) >= new Date() && new Date(o.startDate) <= new Date()).length,
    totalRedemptions: offers.reduce((sum, o) => sum + (o.currentUsageCount || 0), 0),
    totalDiscount: offers.reduce((sum, o) => sum + (o.redemptions?.reduce((s, r) => s + (r.discountAmount || 0), 0) || 0), 0)
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[{ val: stats.total, label: 'Total Offers', icon: <LocalOffer />, bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          { val: stats.active, label: 'Active Offers', icon: <CheckCircle />, bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
          { val: stats.totalRedemptions, label: 'Total Redemptions', icon: <ShoppingCart />, bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
          { val: stats.totalDiscount.toFixed(2), label: 'Total Discount Given', icon: <TrendingUp />, bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ background: s.bg, color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box><Typography variant="h4" fontWeight="bold">{s.val}</Typography><Typography variant="body2" sx={{ opacity: 0.9 }}>{s.label}</Typography></Box>
                  <Box sx={{ fontSize: 48, opacity: 0.3 }}>{s.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><LocalOffer color="primary" /><Typography variant="h6" fontWeight="600">Offers & Promotions</Typography></Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField size="small" placeholder="Search offers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ width: 250 }} />
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchOffers}>Refresh</Button>
            <Button variant="outlined" startIcon={<AutoAwesome />} onClick={handleGenerateAISuggestions} disabled={aiLoading} sx={{ borderColor: '#9c27b0', color: '#9c27b0', '&:hover': { borderColor: '#7b1fa2', bgcolor: 'rgba(156, 39, 176, 0.04)' } }}>{aiLoading ? 'Generating...' : 'AI Suggest'}</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>Create Offer</Button>
          </Box>
        </Box>

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: '1px solid #e0e0e0', px: 2 }}>
          <Tab label={`All (${offers.length})`} />
          <Tab label={`Active (${offers.filter(o => o.isActive && new Date(o.endDate) >= new Date() && new Date(o.startDate) <= new Date()).length})`} />
          <Tab label={`Scheduled (${offers.filter(o => new Date(o.startDate) > new Date()).length})`} />
          <Tab label={`Expired (${offers.filter(o => new Date(o.endDate) < new Date()).length})`} />
          <Tab label={`Inactive (${offers.filter(o => !o.isActive).length})`} />
        </Tabs>

        {loading && <LinearProgress />}

        <TableContainer>
          <Table>
            <TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Offer</TableCell><TableCell>Code</TableCell><TableCell>Type</TableCell><TableCell>Value</TableCell>
              <TableCell>Validity</TableCell><TableCell>Usage</TableCell><TableCell>Status</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filteredOffers.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><LocalOffer sx={{ fontSize: 48, color: '#ccc', mb: 1 }} /><Typography color="textSecondary">No offers found</Typography></TableCell></TableRow>
              ) : filteredOffers.map((offer) => {
                const status = getOfferStatus(offer);
                return (
                  <TableRow key={offer._id} hover>
                    <TableCell><Typography fontWeight="600">{offer.title}</Typography><Typography variant="caption" color="textSecondary">{offer.description?.substring(0, 50)}</Typography></TableCell>
                    <TableCell>{offer.code ? <Chip label={offer.code} size="small" onClick={() => copyToClipboard(offer.code)} onDelete={() => copyToClipboard(offer.code)} deleteIcon={<ContentCopy fontSize="small" />} sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} /> : <Typography variant="caption" color="textSecondary">Auto-apply</Typography>}</TableCell>
                    <TableCell><Chip icon={getOfferTypeIcon(offer.type)} label={offer.type.replace('_', ' ')} size="small" variant="outlined" /></TableCell>
                    <TableCell><Typography fontWeight="600" color="primary">{formatOfferValue(offer)}</Typography>{offer.minOrderValue > 0 && <Typography variant="caption" color="textSecondary" display="block">Min: {offer.minOrderValue}</Typography>}</TableCell>
                    <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CalendarToday fontSize="small" color="action" /><Box><Typography variant="caption" display="block">{new Date(offer.startDate).toLocaleDateString()}</Typography><Typography variant="caption" display="block">to {new Date(offer.endDate).toLocaleDateString()}</Typography></Box></Box></TableCell>
                    <TableCell><Typography variant="body2">{offer.currentUsageCount || 0} / {offer.maxUsageTotal || '∞'}</Typography>{offer.maxUsageTotal && <LinearProgress variant="determinate" value={((offer.currentUsageCount || 0) / offer.maxUsageTotal) * 100} sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />}</TableCell>
                    <TableCell><Chip label={status.label} color={status.color} size="small" /></TableCell>
                    <TableCell align="center">
                      <Tooltip title="Send Offer"><IconButton size="small" onClick={() => handleSendOffer(offer)} color="primary"><Send fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Analytics"><IconButton size="small" onClick={() => handleViewAnalytics(offer)}><TrendingUp fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={offer.isActive ? 'Deactivate' : 'Activate'}><IconButton size="small" onClick={() => handleToggle(offer)}>{offer.isActive ? <ToggleOn color="success" /> : <ToggleOff color="disabled" />}</IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenDialog(offer)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(offer)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocalOffer color="primary" />{selectedOffer ? 'Edit Offer' : 'Create New Offer'}</Box>
          <IconButton onClick={handleCloseDialog}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}><Typography variant="subtitle2" color="primary" gutterBottom>Basic Information</Typography></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Offer Title" name="title" value={formData.title} onChange={handleInputChange} required placeholder="e.g., Summer Sale 20% Off" /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Promo Code" name="code" value={formData.code} onChange={handleInputChange} placeholder="e.g., SUMMER20" InputProps={{ endAdornment: <InputAdornment position="end"><Button size="small" onClick={generatePromoCode}>Generate</Button></InputAdornment>, style: { textTransform: 'uppercase' } }} helperText="Leave empty for auto-apply offers" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleInputChange} multiline rows={2} placeholder="Describe the offer..." /></Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>Discount Details</Typography></Grid>
            <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Discount Type</InputLabel><Select name="type" value={formData.type} onChange={handleInputChange} label="Discount Type">{offerTypes.map(type => <MenuItem key={type.value} value={type.value}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{type.icon}{type.label}</Box></MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label={formData.type === 'PERCENTAGE' ? 'Discount Percentage' : formData.type === 'BUY_X_GET_Y' ? 'Buy Quantity' : 'Discount Amount'} name="value" type="number" value={formData.value} onChange={handleInputChange} required InputProps={{ endAdornment: formData.type === 'PERCENTAGE' ? <InputAdornment position="end">%</InputAdornment> : null }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Minimum Order Value" name="minOrderValue" type="number" value={formData.minOrderValue} onChange={handleInputChange} helperText="Leave empty for no minimum" /></Grid>
            {formData.type === 'PERCENTAGE' && <Grid item xs={12} md={6}><TextField fullWidth label="Maximum Discount Cap" name="maxDiscount" type="number" value={formData.maxDiscount} onChange={handleInputChange} helperText="Leave empty for no cap" /></Grid>}
            <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>Validity Period</Typography></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Start Date" name="startDate" type="date" value={formData.startDate} onChange={handleInputChange} InputLabelProps={{ shrink: true }} required /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="End Date" name="endDate" type="date" value={formData.endDate} onChange={handleInputChange} InputLabelProps={{ shrink: true }} required /></Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>Usage Limits</Typography></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Total Usage Limit" name="maxUsageTotal" type="number" value={formData.maxUsageTotal} onChange={handleInputChange} helperText="Leave empty for unlimited" /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Usage Per Customer" name="maxUsagePerCustomer" type="number" value={formData.maxUsagePerCustomer} onChange={handleInputChange} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))} color="success" />} label="Active" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={handleCloseDialog}>Cancel</Button><Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>{selectedOffer ? 'Update Offer' : 'Create Offer'}</Button></DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onClose={() => setAnalyticsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TrendingUp color="primary" />Offer Analytics</Box><IconButton onClick={() => setAnalyticsDialogOpen(false)}><Close /></IconButton></DialogTitle>
        <DialogContent dividers>
          {selectedOffer && <Box>
            <Typography variant="h6" gutterBottom>{selectedOffer.title}</Typography>
            {selectedOffer.code && <Chip label={selectedOffer.code} size="small" sx={{ mb: 2, fontFamily: 'monospace' }} />}
            {analytics ? (
              <Grid container spacing={2}>
                <Grid item xs={6}><Card sx={{ bgcolor: '#f5f5f5' }}><CardContent><Typography variant="h4" color="primary">{analytics.totalRedemptions}</Typography><Typography variant="body2" color="textSecondary">Total Redemptions</Typography></CardContent></Card></Grid>
                <Grid item xs={6}><Card sx={{ bgcolor: '#f5f5f5' }}><CardContent><Typography variant="h4" color="primary">{analytics.uniqueCustomers}</Typography><Typography variant="body2" color="textSecondary">Unique Customers</Typography></CardContent></Card></Grid>
                <Grid item xs={6}><Card sx={{ bgcolor: '#f5f5f5' }}><CardContent><Typography variant="h4" color="primary">{analytics.totalDiscountGiven?.toFixed(2)}</Typography><Typography variant="body2" color="textSecondary">Total Discount Given</Typography></CardContent></Card></Grid>
                <Grid item xs={6}><Card sx={{ bgcolor: '#f5f5f5' }}><CardContent><Typography variant="h4" color="primary">{analytics.remainingUsage}</Typography><Typography variant="body2" color="textSecondary">Remaining Usage</Typography></CardContent></Card></Grid>
              </Grid>
            ) : <Typography color="textSecondary">Loading analytics...</Typography>}
          </Box>}
        </DialogContent>
      </Dialog>

      {/* AI Suggestions Dialog */}
      <Dialog open={aiSuggestionsOpen} onClose={() => setAiSuggestionsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AutoAwesome />AI-Generated Offer Suggestions</Box>
          <IconButton onClick={() => setAiSuggestionsOpen(false)} sx={{ color: 'white' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {aiSuggestions.length > 0 ? (
            <Box>
              {aiSuggestions.map((suggestion, index) => (
                <Box key={index} sx={{ p: 2, borderBottom: '1px solid #e0e0e0', '&:hover': { bgcolor: '#f5f5f5' }, cursor: 'pointer' }} onClick={() => applyAISuggestion(suggestion)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#9c27b0' }}>{suggestion.title}</Typography>
                      <Typography variant="body2" color="textSecondary">{suggestion.description}</Typography>
                    </Box>
                    <Chip label={suggestion.type?.replace('_', ' ')} size="small" sx={{ bgcolor: '#9c27b0', color: 'white' }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <Chip label={`Code: ${suggestion.suggestedCode}`} size="small" variant="outlined" />
                    <Chip label={`Value: ${suggestion.value}${suggestion.type === 'PERCENTAGE' ? '%' : ''}`} size="small" variant="outlined" color="success" />
                    {suggestion.minOrderValue > 0 && <Chip label={`Min Order: ${suggestion.minOrderValue}`} size="small" variant="outlined" />}
                    <Chip label={`Valid: ${suggestion.validityDays} days`} size="small" variant="outlined" />
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    💡 {suggestion.reasoning}
                  </Typography>
                  <Button size="small" variant="contained" sx={{ mt: 1, background: 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)' }} onClick={(e) => { e.stopPropagation(); applyAISuggestion(suggestion); }}>
                    Use This Suggestion
                  </Button>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">No suggestions available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Offer Dialog */}
      <SendOfferDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        offer={selectedOffer}
      />
    </Box>
  );
};

export default OffersManagement;
